import React from 'react';
import { X, Reply, Trash2, User, Calendar, ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { Message } from '../../types/messaging';

interface MessageViewerProps {
  message: Message;
  onClose: () => void;
  onReply: (message: Message) => void;
  onDelete: (messageId: string) => void;
  onBack?: () => void;
}

export const MessageViewer: React.FC<MessageViewerProps> = ({
  message,
  onClose,
  onReply,
  onDelete,
  onBack
}) => {
  const getProfileTypeColor = (type: string) => {
    const colors = {
      PORTEUR: 'bg-green-100 text-green-800',
      ACCOMPAGNATEUR: 'bg-blue-100 text-blue-800',
      COORDINATEUR: 'bg-purple-100 text-purple-800',
      FINANCEUR: 'bg-orange-100 text-orange-800',
      ADMIN: 'bg-red-100 text-red-800'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getProfileTypeLabel = (type: string) => {
    const labels = {
      PORTEUR: 'Porteur',
      ACCOMPAGNATEUR: 'Accompagnateur',
      COORDINATEUR: 'Coordinateur',
      FINANCEUR: 'Financeur',
      ADMIN: 'Admin'
    };
    return labels[type as keyof typeof labels] || type;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {onBack && (
              <button
                onClick={onBack}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                title="Retour"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{message.subject}</h2>
              <p className="text-sm text-gray-600">
                Message de {message.sender?.full_name || message.sender?.email}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onReply(message)}
              className="flex items-center space-x-1 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Répondre"
            >
              <Reply className="w-4 h-4" />
              <span>Répondre</span>
            </button>
            <button
              onClick={() => {
                if (confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
                  onDelete(message.id);
                  onClose();
                }
              }}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Supprimer"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Message Content */}
        <div className="p-6">
          {/* Sender Info */}
          <div className="flex items-center space-x-3 mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white font-medium">
                {message.sender?.full_name?.charAt(0) || message.sender?.email?.charAt(0) || '?'}
              </span>
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <h3 className="font-semibold text-gray-900">
                  {message.sender?.full_name || message.sender?.email}
                </h3>
                {message.sender?.profile_type && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getProfileTypeColor(message.sender.profile_type)}`}>
                    {getProfileTypeLabel(message.sender.profile_type)}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600">{message.sender?.organization}</p>
              <div className="flex items-center space-x-1 text-xs text-gray-500 mt-1">
                <Calendar className="w-3 h-3" />
                <span>
                  {format(new Date(message.created_at), 'EEEE dd MMMM yyyy à HH:mm', { locale: fr })}
                </span>
              </div>
            </div>
          </div>

          {/* Message Content */}
          <div className="prose prose-blue max-w-none">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <pre className="whitespace-pre-wrap text-gray-800 font-sans leading-relaxed">
                {message.content}
              </pre>
            </div>
          </div>

          {/* Reply Info */}
          {message.parent_message_id && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <Reply className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-800 font-medium">
                  Ce message est une réponse
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            <div className="flex items-center space-x-1">
              <User className="w-4 h-4" />
              <span>
                Pour : {message.recipient?.full_name || message.recipient?.email || 'Vous'}
              </span>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => onReply(message)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Reply className="w-4 h-4" />
              <span>Répondre</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};